import type { GetMyPassengerResponse } from '@massilia/queries'

import {
  DEFAULT_OPTION,
  NAME_TOOLTIP_TEXT,
  NationalityAutocompleteField,
  useAppForm,
} from '@massilia/features'
import { useCreateMyPassengerMutation, useUpdateMyPassengerMutation } from '@massilia/queries'
import { Button, Dialog, DialogContent, DialogHeader, DialogTitle } from '@massilia/ui'
import { revalidateLogic, standardSchemaValidators } from '@tanstack/react-form'
import { useEffect } from 'react'
import { toast } from 'sonner'

import { resolveAuthErrorMessage } from '@/lib/auth-error-message'
import { passengerSchema } from '@/lib/schemas/personal-informations/personal-informations.schemas'

interface PassengerFormDialogProps {
  isOpen: boolean
  onClose: () => void
  passenger?: GetMyPassengerResponse
}

const GENDER_OPTIONS = [
  { label: 'Homme', value: 'MALE' },
  { label: 'Femme', value: 'FEMALE' },
] as const

export function PassengerFormDialog({ isOpen, onClose, passenger }: PassengerFormDialogProps) {
  const isEditing = !!passenger
  const { mutateAsync: createPassenger } = useCreateMyPassengerMutation()
  const { mutateAsync: updatePassenger } = useUpdateMyPassengerMutation()

  const form = useAppForm({
    defaultValues: {
      firstName: passenger?.firstName ?? '',
      lastName: passenger?.lastName ?? '',
      birthDate: passenger?.birthDate ?? (null as null | string),
      gender: (passenger?.gender ?? 'MALE') as 'FEMALE' | 'MALE',
      nationality: passenger?.nationalityRef
        ? {
            label: passenger.nationalityRef.nationalityFr,
            value: passenger.nationalityRef.alpha3Code,
          }
        : DEFAULT_OPTION,
    },
    onSubmit: async ({ value }) => {
      try {
        const nationalityCode = value.nationality.value
        if (isEditing && passenger) {
          await updatePassenger({
            passengerId: passenger.id,
            data: {
              firstName: value.firstName,
              lastName: value.lastName,
              birthDate: value.birthDate ?? undefined,
              gender: value.gender,
              nationalityCode,
            },
          })
        } else {
          await createPassenger({
            firstName: value.firstName,
            lastName: value.lastName,
            birthDate: value.birthDate ?? '',
            gender: value.gender,
            nationalityCode,
          })
        }
        onClose()
      } catch (error) {
        toast.error(resolveAuthErrorMessage(error))
      }
    },
    validationLogic: revalidateLogic({
      mode: 'submit',
      modeAfterSubmission: 'blur',
    }),
    validators: {
      onDynamic: ({ value }) =>
        standardSchemaValidators.validate({ validationSource: 'form', value }, passengerSchema),
    },
  })

  useEffect(() => {
    if (!isOpen) {
      const timeout = setTimeout(() => {
        form.reset()
      }, 200)
      return () => clearTimeout(timeout)
    }
  }, [isOpen, form])

  return (
    <Dialog modal={false} onOpenChange={(open) => !open && onClose()} open={isOpen}>
      <DialogContent onOverlayClick={onClose} className="gap-6 rounded-2xl bg-white p-8">
        <DialogHeader>
          <DialogTitle className="text-center text-xl font-semibold">
            {isEditing ? 'Modifier le passager' : 'Ajouter un passager'}
          </DialogTitle>
        </DialogHeader>

        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault()
            e.stopPropagation()
            void form.handleSubmit()
          }}
        >
          <form.AppField
            name="gender"
            children={(field) => (
              <field.RadioGroupField
                label="Sexe"
                options={GENDER_OPTIONS}
                orientation="horizontal"
              />
            )}
          />
          <form.AppField
            name="firstName"
            children={(field) => (
              <field.TextField
                label="Prénom"
                placeholder="Prénom"
                questionToolTip={NAME_TOOLTIP_TEXT}
              />
            )}
          />
          <form.AppField
            name="lastName"
            children={(field) => (
              <field.TextField
                label="Nom de famille"
                placeholder="Nom"
                questionToolTip={NAME_TOOLTIP_TEXT}
              />
            )}
          />
          <form.AppField
            name="birthDate"
            children={(field) => <field.DateField label="Date de naissance" />}
          />
          <form.AppField
            name="nationality"
            children={() => <NationalityAutocompleteField label="Nationalité" />}
          />

          <form.Subscribe selector={(state) => state.isSubmitting}>
            {(isSubmitting) => (
              <Button
                className="bg-brand-600 hover:bg-brand-700 w-full text-white"
                disabled={isSubmitting}
                type="submit"
              >
                {isSubmitting ? 'Enregistrement...' : isEditing ? 'Modifier' : 'Ajouter'}
              </Button>
            )}
          </form.Subscribe>
        </form>
      </DialogContent>
    </Dialog>
  )
}
